import { useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

import { type AnyDate } from '@/shared/types/common';

import { normalizeToValue } from '../helpers/function';

dayjs.extend(duration);
dayjs.extend(utc);

export const DEFAULT_UPDATE_COUNTDOWN_INTERVAL_MS = 1_000;

export enum CountdownParts {
  years = 'years',
  months = 'months',
  days = 'days',
  hours = 'hours',
  minutes = 'minutes',
  seconds = 'seconds',
  milliseconds = 'milliseconds',
}

const POSSIBLE_COUNTDOWN_PARTS = Object.values(CountdownParts);

export interface UseCountdownProps {
  pause?: boolean;
  interval?: number;
  onEnd?: () => void;
}

export const useCountdown = <T extends AnyDate | null | undefined>(
  value: (() => T) | T,
  {
    interval = DEFAULT_UPDATE_COUNTDOWN_INTERVAL_MS,
    onEnd,
    pause,
  }: UseCountdownProps = {}
) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [currentDate, setCurrentDate] = useState(dayjs());

  const targetDate = useMemo(
    () => (value ? dayjs.utc(normalizeToValue(value)).local() : null),
    [value]
  );

  useEffect(() => {
    if (pause || !targetDate) return;

    setCurrentDate(dayjs());

    intervalRef.current = setInterval(() => {
      setCurrentDate(dayjs());
    }, interval);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [value, pause, interval, targetDate]);

  // Stop interval when the target date is reached
  useEffect(() => {
    if (pause || !targetDate) return;

    if (currentDate.valueOf() >= targetDate.valueOf()) {
      if (intervalRef.current) clearInterval(intervalRef.current);

      onEnd?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const differenceDuration = useMemo(() => {
    if (!targetDate) return { get: () => 0 };

    if (currentDate.isAfter(targetDate)) {
      return dayjs.duration(currentDate.diff(currentDate));
    }

    return dayjs.duration(targetDate.diff(currentDate));
  }, [currentDate, targetDate]);

  return useMemo(
    () =>
      Object.fromEntries(
        POSSIBLE_COUNTDOWN_PARTS.map<[CountdownParts, number]>((part) => [
          part,
          Math.abs(differenceDuration.get(part)),
        ])
      ) as Record<CountdownParts, number>,
    [differenceDuration]
  );
};
